import React from 'react';
import { Box, Typography, Chip, Tooltip, Divider, IconButton, CircularProgress, Button, useTheme, useMediaQuery } from '@mui/material';
import { UnfoldMore, UnfoldLess, Stop as StopIcon } from '@mui/icons-material';
import { consistentSpinnerSx } from '../types';

interface LogStatsBarProps {
  totalLines: number;
  streams: number;
  filteredCount: number;
  hasMoreLogs: boolean;
  isZoomed: boolean;
  commonLabels: Record<string, string>;
  loadingMore: boolean;
  loadingAllLogs: boolean;
  liveReloading: boolean;
  allRowsExpanded: boolean;
  onToggleAllRows: () => void;
  onLoadAllLogs: () => void;
  onStopLoadAllLogs: () => void;
}

export const LogStatsBar: React.FC<LogStatsBarProps> = React.memo(({
  totalLines,
  streams,
  filteredCount,
  hasMoreLogs,
  isZoomed,
  commonLabels,
  loadingMore,
  loadingAllLogs,
  liveReloading,
  allRowsExpanded,
  onToggleAllRows,
  onLoadAllLogs,
  onStopLoadAllLogs,
}) => {
  const theme = useTheme();
  const isMobilePortrait = useMediaQuery(theme.breakpoints.down('sm'));
  const isLandscapeMobile = useMediaQuery('(max-height: 500px) and (orientation: landscape)');
  const isCompact = isMobilePortrait || isLandscapeMobile;

  return (
    <Box
      sx={{
        px: 2, py: 1,
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        borderBottom: '1px solid', borderColor: 'divider',
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {isZoomed ? (
          <>Showing <strong>{filteredCount}</strong> of <strong>{totalLines}</strong> log lines (zoomed)</>
        ) : (
          <>
            <strong>{totalLines}</strong> log lines from <strong>{streams}</strong> stream{streams !== 1 ? 's' : ''}
            {hasMoreLogs && !loadingAllLogs && <> &mdash; scroll down for more</>}
            {loadingAllLogs && <> &mdash; loading all pages... (click Stop to cancel)</>}
          </>
        )}
      </Typography>
      {hasMoreLogs && !isZoomed && (
        <Button
          size="small"
          variant={loadingAllLogs ? 'contained' : 'outlined'}
          color={loadingAllLogs ? 'error' : 'secondary'}
          onClick={loadingAllLogs ? onStopLoadAllLogs : onLoadAllLogs}
          disabled={loadingMore && !loadingAllLogs}
          startIcon={loadingAllLogs ? <StopIcon sx={{ fontSize: 14 }} /> : undefined}
          sx={{
            textTransform: 'none', fontSize: '0.7rem', py: 0, px: 1,
            minWidth: 'auto', lineHeight: 1.6, fontWeight: 600,
            '& .MuiButton-startIcon': { mr: 0.25 },
          }}
        >
          {loadingAllLogs ? 'Stop' : 'All pages'}
        </Button>
      )}
      {Object.keys(commonLabels).length > 0 && !isCompact && (
        <>
          <Divider orientation="vertical" flexItem />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            Common labels:
            {Object.entries(commonLabels).map(([k, v]) => (
              <Chip
                key={k}
                label={`${k}=${v}`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', fontFamily: 'monospace', borderColor: 'text.disabled' }}
              />
            ))}
          </Typography>
        </>
      )}
      <Box sx={{ flex: 1 }} />
      {(loadingMore || liveReloading || loadingAllLogs) && (
        <Tooltip title={loadingAllLogs ? 'Loading all log pages...' : liveReloading ? 'Live: fetching new logs...' : 'Loading more logs...'}>
          <CircularProgress size={16} variant="determinate" value={30} color="secondary" sx={consistentSpinnerSx} />
        </Tooltip>
      )}
      {!isCompact && (
        <Tooltip title={allRowsExpanded ? 'Collapse all' : 'Expand all'}>
          <IconButton size="small" onClick={onToggleAllRows}>
            {allRowsExpanded ? <UnfoldLess fontSize="small" /> : <UnfoldMore fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
});

LogStatsBar.displayName = 'LogStatsBar';
